import React, { useState, useEffect, useCallback } from 'react';
import './ImageLightbox.css';

const ImageLightbox = ({ 
  isOpen, 
  images, 
  currentIndex, 
  itemName, 
  onClose, 
  onIndexChange 
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(currentIndex || 0);

  useEffect(() => {
    setCurrentImageIndex(currentIndex || 0);
  }, [currentIndex]);

  const handlePrevious = useCallback(() => {
    const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : images.length - 1;
    setCurrentImageIndex(newIndex);
    if (onIndexChange) onIndexChange(newIndex);
  }, [currentImageIndex, images.length, onIndexChange]);

  const handleNext = useCallback(() => {
    const newIndex = currentImageIndex < images.length - 1 ? currentImageIndex + 1 : 0;
    setCurrentImageIndex(newIndex);
    if (onIndexChange) onIndexChange(newIndex);
  }, [currentImageIndex, images.length, onIndexChange]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;
      
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentImageIndex, onClose, handleNext, handlePrevious]);

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
    if (onIndexChange) onIndexChange(index);
  };

  if (!isOpen || !images || images.length === 0) {
    return null;
  }

  const currentImage = images[currentImageIndex];

  return (
    <div className="image-lightbox-overlay" onClick={onClose}>
      <div className="image-lightbox-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="image-lightbox-header">
          <h3 className="image-lightbox-title">{itemName}</h3>
          <button className="image-lightbox-close" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Main Image */}
        <div className="image-lightbox-main">
          <img 
            src={currentImage} 
            alt={`${itemName} ${currentImageIndex + 1}`}
            className="image-lightbox-image"
          />
          
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button 
                className="image-lightbox-nav image-lightbox-prev" 
                onClick={handlePrevious}
                aria-label="Previous image"
              >
                ‹
              </button>
              <button 
                className="image-lightbox-nav image-lightbox-next" 
                onClick={handleNext}
                aria-label="Next image"
              >
                ›
              </button>
            </>
          )}
        </div>

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="image-lightbox-counter">
            {currentImageIndex + 1} / {images.length}
          </div>
        )}

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="image-lightbox-thumbnails">
            {images.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className={`image-lightbox-thumbnail ${
                  index === currentImageIndex ? 'active' : ''
                }`}
                onClick={() => handleThumbnailClick(index)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageLightbox;
